import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Avatar,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Tabs,
  Space,
} from 'antd';
import {
  UserOutlined,
  SaveOutlined,
  LockOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { authApi } from '../../services/auth';
import type { UpdateUserRequest, ChangePasswordRequest } from '../../types/auth';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;

export const Profile: React.FC = () => {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const { user, fetchUserInfo } = useAuthStore();

  // 初始化表单值
  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        name: user.name,
        email: user.email,
      });
    }
  }, [user, profileForm]);

  // 保存个人资料
  const handleSaveProfile = async (values: UpdateUserRequest) => {
    setProfileLoading(true);
    try {
      await authApi.updateCurrentUser(values);
      await fetchUserInfo();
      message.success('个人资料更新成功');
    } catch {
      message.error('更新失败，请重试');
    } finally {
      setProfileLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async (values: ChangePasswordRequest) => {
    setPasswordLoading(true);
    try {
      await authApi.changePassword(values);
      message.success('密码修改成功');
      passwordForm.resetFields();
    } catch {
      message.error('密码修改失败，请检查当前密码是否正确');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 个人资料 Tab
  const ProfileTab = (
    <Card>
      <Row gutter={48}>
        <Col span={8} style={{ textAlign: 'center' }}>
          <Avatar
            size={120}
            src={user?.avatar}
            icon={!user?.avatar && <UserOutlined />}
            style={{ backgroundColor: user?.avatar ? undefined : COLORS.primary, marginBottom: 16 }}
          />
          <div>
            <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 8 }}>
              {user?.name || '用户'}
            </Text>
            <Text type="secondary">{user?.email}</Text>
          </div>
          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              角色: {user?.role === 'owner' ? '所有者' : user?.role === 'admin' ? '管理员' : user?.role === 'operator' ? '操作员' : '查看者'}
            </Text>
          </div>
        </Col>
        <Col span={16}>
          <Title level={5} style={{ marginBottom: 24 }}>
            基本信息
          </Title>
          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleSaveProfile}
          >
            <Form.Item
              name="name"
              label="姓名"
              rules={[{ required: true, message: '请输入姓名' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="请输入姓名" />
            </Form.Item>

            <Form.Item
              name="email"
              label="邮箱"
            >
              <Input prefix={<MailOutlined />} disabled />
            </Form.Item>

            <Form.Item
              name="avatar"
              label="头像 URL"
            >
              <Input placeholder="输入头像图片 URL（可选）" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={profileLoading}
              >
                保存修改
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Card>
  );

  // 修改密码 Tab
  const PasswordTab = (
    <Card style={{ maxWidth: 500 }}>
      <Title level={5} style={{ marginBottom: 24 }}>
        修改密码
      </Title>
      <Form
        form={passwordForm}
        layout="vertical"
        onFinish={handleChangePassword}
      >
        <Form.Item
          name="current_password"
          label="当前密码"
          rules={[{ required: true, message: '请输入当前密码' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请输入当前密码"
          />
        </Form.Item>

        <Form.Item
          name="new_password"
          label="新密码"
          rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码长度至少为6位' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请输入新密码"
          />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label="确认新密码"
          rules={[
            { required: true, message: '请确认新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('两次输入的密码不一致'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请再次输入新密码"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<LockOutlined />}
            loading={passwordLoading}
          >
            修改密码
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  const tabItems = [
    {
      key: 'profile',
      label: (
        <Space>
          <UserOutlined />
          个人资料
        </Space>
      ),
      children: ProfileTab,
    },
    {
      key: 'password',
      label: (
        <Space>
          <LockOutlined />
          修改密码
        </Space>
      ),
      children: PasswordTab,
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        个人设置
      </Title>
      <Tabs items={tabItems} defaultActiveKey="profile" />
    </div>
  );
};
